import ApiClient from "../helpers/ApiClient";
import useSWR from "swr";

const fetcher = (url) => ApiClient.get(url).then((res) => res.data);

export function useRequisitions({ status = "all", mode = "all", from = "", to = "", search = "", page = 1, limit = 20 } = {}) {
  const queryParams = new URLSearchParams();
  if (status && status !== "all") queryParams.append("status", status);
  if (mode && mode !== "all") queryParams.append("mode", mode);
  if (from) queryParams.append("from", from);
  if (to) queryParams.append("to", to);
  if (search) queryParams.append("search", search);
  queryParams.append("page", page);
  queryParams.append("limit", limit);

  const APIURL = `/requisitions?${queryParams.toString()}`;
  const { data, error, isLoading, mutate } = useSWR(APIURL, fetcher);

  const requisitionList = Array.isArray(data?.requisitions)
    ? data.requisitions
    : Array.isArray(data)
    ? data
    : [];

  return {
    data: requisitionList,
    total: typeof data?.total === "number" ? data.total : requisitionList.length,
    page: data?.page || 1,
    limit: data?.limit || 20,
    error,
    isLoading,
    mutate,
    APIURL,
  };
}

export function useRequisitionDetail(id) {
  const APIURL = id ? `/requisitions/${id}` : null;
  const { data, error, isLoading, mutate } = useSWR(APIURL, fetcher);

  return {
    requisition: data || null,
    error,
    isLoading,
    mutate,
    APIURL,
  };
}

export async function createRequisition(data) {
  return await ApiClient.post("/requisitions", data);
}

export async function updateRequisition(id, data) {
  return await ApiClient.put(`/requisitions/${id}`, data);
}

export async function submitRequisitionForApproval(id) {
  return await ApiClient.post(`/requisitions/${id}/submit`);
}

export async function cancelRequisition(id, reason = "") {
  return await ApiClient.post(`/requisitions/${id}/cancel`, { reason });
}

export async function approveRequisition(id, approvalData) {
  return await ApiClient.post(`/requisitions/${id}/approve`, approvalData);
}

export async function forwardRequisitionToProvider(id, notes = "") {
  return await ApiClient.post(`/requisitions/${id}/forward`, { notes });
}

export async function recordRequisitionReceiving(id, receivingData) {
  return await ApiClient.post(`/requisitions/${id}/receive`, receivingData);
}

// ──────────────────────────────────────────────────────────────
// Super Admin Supply Orders
// ──────────────────────────────────────────────────────────────
export function useProviderSupplyOrders({ tenant_id = "", status = "all", from = "", to = "", page = 1, limit = 20 } = {}) {
  const queryParams = new URLSearchParams();
  if (tenant_id) queryParams.append("tenant_id", tenant_id);
  if (status && status !== "all") queryParams.append("status", status);
  if (from) queryParams.append("from", from);
  if (to) queryParams.append("to", to);
  queryParams.append("page", page);
  queryParams.append("limit", limit);

  const APIURL = `/superadmin/supply-orders?${queryParams.toString()}`;
  const { data, error, isLoading, mutate } = useSWR(APIURL, fetcher);

  return {
    orders: data?.orders || [],
    total: data?.total || 0,
    page: data?.page || 1,
    limit: data?.limit || 20,
    error,
    isLoading,
    mutate,
    APIURL,
  };
}

export function useProviderSupplyOrderDetail(id) {
  const APIURL = id ? `/superadmin/supply-orders/${id}` : null;
  const { data, error, isLoading, mutate } = useSWR(APIURL, fetcher);

  return {
    order: data || null,
    error,
    isLoading,
    mutate,
    APIURL,
  };
}

export async function markProviderOrderDispatched(id, dispatchData = {}) {
  return await ApiClient.post(`/superadmin/supply-orders/${id}/dispatch`, dispatchData);
}
